import { curveCatmullRom, curveLinear } from '@visx/curve';
import { LinePath } from '@visx/shape';
import { useEffect, useRef } from 'react';

import { chartConfig } from '@config/chart';
import { useOrders } from '@hooks/useOrders';
import type { Asset } from '@shared/types';

import { formatStrikePrice } from '../../../utils/chartHelpers';

import { getVisibleStrikes, isVisibleStrike } from './densityUtils';
import { DataPoint, XScale, YScale } from './types';

interface PriceLineProps {
  data: DataPoint[];
  xScale: XScale;
  yScale: YScale;
  color: string;
  animate?: boolean;
}

export function PriceLine({ data, xScale, yScale, color, animate = false }: PriceLineProps) {
  const groupRef = useRef<SVGGElement>(null);

  useEffect(() => {
    if (animate && groupRef.current) {
      // Find the path element inside the group
      const pathElement = groupRef.current.querySelector('path') as SVGPathElement | null;
      if (pathElement) {
        // Initially hide the path
        pathElement.style.opacity = '0';
        
        // Use requestAnimationFrame to ensure the path is rendered before calculating length
        requestAnimationFrame(() => {
          const pathLength = pathElement.getTotalLength();
          pathElement.style.setProperty('--path-length', `${pathLength}px`);
          pathElement.style.strokeDasharray = `var(--path-length)`;
          pathElement.style.strokeDashoffset = `var(--path-length)`;
          pathElement.style.opacity = '1';
          pathElement.style.animation = 'drawLine 1s linear forwards';
        });
      }
    }
  }, [animate, data, xScale, yScale]);

  // Always render if we have at least one point (even if all prices are the same)
  // This ensures flat lines at minimum price are visible
  if (data.length === 0) {
    return null;
  }
  
  // If only one point, we can't draw a line, but the component should handle this
  // For flat lines (all same price), we need at least 2 points to draw
  if (data.length === 1) {
    return null;
  }

  // Check if all prices are the same (flat line)
  const prices = data.map(d => d.price);
  const allPricesSame = prices.every(p => p === prices[0]);
  
  // Use curveLinear for flat lines to ensure proper rendering
  // Use curveCatmullRom for lines with price variation for smooth curves
  const curveType = allPricesSame ? curveLinear : curveCatmullRom;

  return (
    <g ref={groupRef}>
      <defs>
        <filter id="price-line-shadow">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation={chartConfig.priceLine.shadowBlur}
            floodOpacity="1"
            floodColor="black"
          />
        </filter>
        {animate && (
          <style>
            {`
              @keyframes drawLine {
                from {
                  stroke-dashoffset: var(--path-length);
                }
                to {
                  stroke-dashoffset: 0;
                }
              }
            `}
          </style>
        )}
      </defs>
      <LinePath<DataPoint>
        data={data}
        x={d => xScale(d.timestamp)}
        y={d => yScale(d.price)}
        stroke={color}
        strokeWidth={chartConfig.priceLine.strokeWidth}
        fill="none"
        curve={curveType}
        strokeLinecap="round"
        filter="url(#price-line-shadow)"
        style={animate ? { opacity: 0 } : undefined}
      />
    </g>
  );
}

interface CurrentPriceCircleProps {
  x: number;
  y: number;
  color: string;
}

export function CurrentPriceCircle({ x, y, color }: CurrentPriceCircleProps) {
  return (
    <g>
      <defs>
        <filter
          id="price-circle-shadow"
          x="-50%"
          y="-50%"
          width="200%"
          height="200%"
        >
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation={chartConfig.priceLine.currentPrice.shadowBlur}
            floodOpacity="0.75"
            floodColor="black"
          />
        </filter>
      </defs>
      <g filter="url(#price-circle-shadow)">
        {/* Expanding pulse ring */}
        <circle
          cx={x}
          cy={y}
          r={chartConfig.priceLine.currentPrice.pulseRadius}
          fill="none"
          stroke={color}
          strokeWidth={2}
          opacity={0}
          style={{
            animation: 'expandingPulse 2s ease-out infinite',
            transformOrigin: `${x}px ${y}px`,
          }}
        />

        {/* Core circles */}
        <circle
          cx={x}
          cy={y}
          r={chartConfig.priceLine.currentPrice.circleRadius.outer}
          fill={color}
          opacity={chartConfig.priceLine.currentPrice.opacity.outer}
        />
        <circle
          cx={x}
          cy={y}
          r={chartConfig.priceLine.currentPrice.circleRadius.inner}
          fill={color}
          opacity={chartConfig.priceLine.currentPrice.opacity.inner}
        />
      </g>
    </g>
  );
}

interface GridLinesProps {
  strikePrices: number[];
  yScale: YScale;
  innerWidth: number;
  innerHeight: number;
  yScaleDomain: [number, number];
  labelsOnly?: boolean;
  selectedStrike?: number;
  spotPrice?: number;
  asset: Asset;
  isDraggingStrike?: boolean;
  nearestValidStrike?: number | null;
}

export function GridLines({
  strikePrices,
  yScale,
  innerWidth,
  innerHeight,
  yScaleDomain,
  labelsOnly = false,
  selectedStrike,
  spotPrice,
  asset,
  isDraggingStrike = false,
  nearestValidStrike = null,
}: GridLinesProps) {
  const hasStrikes = strikePrices.length > 0;
  const { openOrders } = useOrders();

  // Function to determine strike line color based on pending orders
  const getStrikeLineColor = (strikePrice: number): string => {
    // Filter orders for this asset and strike price
    const ordersAtStrike = openOrders.filter(
      order => order.asset === asset && order.strikePrice === strikePrice,
    );

    if (ordersAtStrike.length === 0) {
      // No orders at this strike - use default white
      return 'rgba(255, 255, 255, {{opacity}})';
    }

    const callOrders = ordersAtStrike.filter(order => order.type === 'CALL');
    const putOrders = ordersAtStrike.filter(order => order.type === 'PUT');

    if (callOrders.length > 0 && putOrders.length > 0) {
      // Both CALL and PUT orders - return special case for split coloring
      return 'MIXED';
    } else if (callOrders.length > 0) {
      // Only CALL orders - green
      return 'rgba(var(--brand-green-rgb), {{opacity}})';
    } else {
      // Only PUT orders - red
      return 'rgba(var(--brand-red-rgb), {{opacity}})';
    }
  };

  // Use density filtering to get strikes that should be visible
  const visibleStrikesSet = hasStrikes
    ? getVisibleStrikes(strikePrices, yScale, innerHeight, selectedStrike)
    : new Set<number>();
  const densityFilteredStrikes = Array.from(visibleStrikesSet).sort(
    (a, b) => a - b,
  );

  // Find the closest strike to spot price when no strike is selected
  let closestStrike: number | null = null;
  if (!selectedStrike && hasStrikes && strikePrices.length > 0 && spotPrice) {
    closestStrike = strikePrices.reduce((prev, curr) =>
      Math.abs(curr - spotPrice) < Math.abs(prev - spotPrice) ? curr : prev,
    );
  }

  // Generate grid lines with 4 lines between each strike
  const generateGridLines = (): number[] => {
    const [domainMin, domainMax] = yScaleDomain;
    const lines = [];

    if (!hasStrikes || densityFilteredStrikes.length === 0) {
      // Fallback: evenly spaced lines when no strikes
      const range = domainMax - domainMin;
      const step = range / 10;
      for (let i = 0; i <= 10; i++) {
        lines.push(domainMin + step * i);
      }
      return lines;
    }

    // Use density-filtered strikes within the visible range
    const visibleStrikes = densityFilteredStrikes
      .filter(price => price >= domainMin && price <= domainMax)
      .sort((a, b) => a - b);

    if (visibleStrikes.length === 0) {
      // No strikes in range, use fallback
      const range = domainMax - domainMin;
      const step = range / 10;
      for (let i = 0; i <= 10; i++) {
        lines.push(domainMin + step * i);
      }
      return lines;
    }

    // Add all density-filtered visible strikes
    lines.push(...visibleStrikes);

    // Calculate if we're zoomed in enough to show subdivision lines
    if (visibleStrikes.length > 1) {
      const avgStrikeSpacing =
        (visibleStrikes[visibleStrikes.length - 1] - visibleStrikes[0]) /
        (visibleStrikes.length - 1);

      // Only add subdivision lines if we're zoomed in enough
      if (
        visibleStrikes.length <
        chartConfig.gridLines.maxVisibleStrikesForSubdivision
      ) {
        // Add 4 lines between each consecutive strike
        for (let i = 0; i < visibleStrikes.length - 1; i++) {
          const start = visibleStrikes[i];
          const end = visibleStrikes[i + 1];
          const spacing = (end - start) / 5; // 5 sections = 4 lines between

          for (let j = 1; j <= 4; j++) {
            lines.push(start + spacing * j);
          }
        }

        // Extend pattern before first strike if needed
        const firstStrike = visibleStrikes[0];
        const spacing =
          visibleStrikes.length > 1
            ? visibleStrikes[1] - visibleStrikes[0]
            : avgStrikeSpacing;
        const subdivisionSpacing = spacing / 5;

        let current = firstStrike - subdivisionSpacing;
        while (current >= domainMin) {
          lines.push(current);
          current -= subdivisionSpacing;
        }

        // Extend pattern after last strike if needed
        const lastStrike = visibleStrikes[visibleStrikes.length - 1];
        const lastSpacing =
          visibleStrikes.length > 1
            ? lastStrike - visibleStrikes[visibleStrikes.length - 2]
            : avgStrikeSpacing;
        const lastSubdivisionSpacing = lastSpacing / 5;

        current = lastStrike + lastSubdivisionSpacing;
        while (current <= domainMax) {
          lines.push(current);
          current += lastSubdivisionSpacing;
        }
      }
    } else {
      // Single strike visible, extend pattern in both directions
      const strike = visibleStrikes[0];
      // Use typical strike spacing if available
      const avgStrikeSpacing =
        strikePrices.length > 1
          ? (Math.max(...strikePrices) - Math.min(...strikePrices)) /
            (strikePrices.length - 1)
          : (domainMax - domainMin) / 10;

      const subdivisionSpacing = avgStrikeSpacing / 5;

      // Extend in both directions
      let current = strike - subdivisionSpacing;
      while (current >= domainMin) {
        lines.push(current);
        current -= subdivisionSpacing;
      }

      current = strike + subdivisionSpacing;
      while (current <= domainMax) {
        lines.push(current);
        current += subdivisionSpacing;
      }
    }

    // Remove duplicates and sort
    return [...new Set(lines)].sort((a, b) => a - b);
  };

  const allLines = generateGridLines();

  return (
    <>
      {allLines.map(price => {
        const y = yScale(price);
        const isMainStrike = hasStrikes && strikePrices.includes(price);
        const isVisibleMainStrike = isVisibleStrike(price, visibleStrikesSet);

        // For main strikes: Only show if they passed density filtering
        // For subdivision lines: Show if they're between visible strikes OR within reasonable range of edges
        let shouldShowStrikeLine = false;
        if (isMainStrike) {
          shouldShowStrikeLine = isVisibleMainStrike;
        } else {
          // For subdivision lines, check if they're between two visible strikes
          const sortedVisibleStrikes = densityFilteredStrikes.sort(
            (a, b) => a - b,
          );
          // Check if between visible strikes
          for (let i = 0; i < sortedVisibleStrikes.length - 1; i++) {
            if (
              price > sortedVisibleStrikes[i] &&
              price < sortedVisibleStrikes[i + 1]
            ) {
              shouldShowStrikeLine = true;
              break;
            }
          }

          // Also show subdivision lines that extend beyond the first/last visible strikes
          // within the current chart domain to avoid awkward visual gaps
          if (!shouldShowStrikeLine && sortedVisibleStrikes.length > 0) {
            const firstVisibleStrike = sortedVisibleStrikes[0];
            const lastVisibleStrike =
              sortedVisibleStrikes[sortedVisibleStrikes.length - 1];
            const [domainMin, domainMax] = yScaleDomain;

            // Show lines before first visible strike if within domain
            if (price < firstVisibleStrike && price >= domainMin) {
              shouldShowStrikeLine = true;
            }

            // Show lines after last visible strike if within domain
            if (price > lastVisibleStrike && price <= domainMax) {
              shouldShowStrikeLine = true;
            }
          }
        }

        // Different opacity based on whether we have strikes and if this is a main strike
        let opacity;
        let shouldGlow = false;
        if (!hasStrikes) {
          opacity = chartConfig.gridLines.opacity.noStrikes;
        } else if (isVisibleMainStrike) {
          // Check if this is the closest strike and no strike is selected
          shouldGlow = !selectedStrike && closestStrike === price;
          opacity = shouldGlow
            ? chartConfig.gridLines.opacity.closestStrike
            : chartConfig.gridLines.opacity.mainStrike;
        } else {
          opacity = chartConfig.gridLines.opacity.subdivision;
        }

        // Show price labels on subdivision lines when very zoomed in
        const showPriceLabel =
          !isMainStrike &&
          hasStrikes &&
          shouldShowStrikeLine &&
          densityFilteredStrikes.filter(
            strike => strike >= yScaleDomain[0] && strike <= yScaleDomain[1],
          ).length <= 3;

        const centerX = innerWidth / 2;
        // Calculate dynamic width based on text length - more padding for longer prices
        const priceText = formatStrikePrice(price);
        const baseWidth = Math.max(60, priceText.length * 8 + 20); // Minimum 60px, 8px per char + 20px padding
        const labelWidth = baseWidth;
        const labelHalfWidth = labelWidth / 2;

        // Get the strike line color based on pending orders
        const strikeColorTemplate = isMainStrike
          ? getStrikeLineColor(price)
          : 'rgba(255, 255, 255, {{opacity}})';
        const isMixedOrders = strikeColorTemplate === 'MIXED';

        // For mixed orders, we'll use green for top half and red for bottom half
        const topStrokeColor = isMixedOrders
          ? `rgba(var(--brand-green-rgb), ${opacity})`
          : strikeColorTemplate.replace('{{opacity}}', opacity.toString());
        const bottomStrokeColor = isMixedOrders
          ? `rgba(var(--brand-red-rgb), ${opacity})`
          : strikeColorTemplate.replace('{{opacity}}', opacity.toString());

        // Hide native strike line when showing ghost at this position
        const hideNativeStrike =
          isDraggingStrike &&
          nearestValidStrike !== null &&
          Math.abs(price - nearestValidStrike) < 0.01;

        return (
          <g key={price}>
            {!labelsOnly && shouldShowStrikeLine && !hideNativeStrike && (
              <>
                {isMainStrike ? (
                  // For main strikes: Split line with gap for centered label
                  <>
                    {isMixedOrders ? (
                      // Mixed orders: Green on top half, red on bottom half
                      <>
                        <line
                          x1={0}
                          y1={y - 1}
                          x2={centerX - labelHalfWidth}
                          y2={y - 1}
                          stroke={topStrokeColor}
                          strokeDasharray="2,2"
                        />
                        <line
                          x1={0}
                          y1={y + 1}
                          x2={centerX - labelHalfWidth}
                          y2={y + 1}
                          stroke={bottomStrokeColor}
                          strokeDasharray="2,2"
                        />
                        <line
                          x1={centerX + labelHalfWidth}
                          y1={y - 1}
                          x2={innerWidth}
                          y2={y - 1}
                          stroke={topStrokeColor}
                          strokeDasharray="2,2"
                        />
                        <line
                          x1={centerX + labelHalfWidth}
                          y1={y + 1}
                          x2={innerWidth}
                          y2={y + 1}
                          stroke={bottomStrokeColor}
                          strokeDasharray="2,2"
                        />
                      </>
                    ) : (
                      // Single color orders
                      <>
                        <line
                          x1={0}
                          y1={y}
                          x2={centerX - labelHalfWidth}
                          y2={y}
                          stroke={topStrokeColor}
                          strokeDasharray="2,2"
                        />
                        <line
                          x1={centerX + labelHalfWidth}
                          y1={y}
                          x2={innerWidth}
                          y2={y}
                          stroke={topStrokeColor}
                          strokeDasharray="2,2"
                        />
                      </>
                    )}

                    {/* Centered strike price label with dark transparent background */}
                    <g style={{ pointerEvents: 'none' }}>
                      <rect
                        x={centerX - labelHalfWidth}
                        y={y - chartConfig.gridLines.label.height / 2}
                        width={labelWidth}
                        height={chartConfig.gridLines.label.height}
                        rx={chartConfig.gridLines.label.borderRadius}
                        fill={chartConfig.gridLines.label.background}
                        stroke={chartConfig.gridLines.label.border}
                        strokeWidth={1}
                      />
                      <text
                        x={centerX}
                        y={y}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fill="rgba(255, 255, 255, 0.9)"
                        fontSize={chartConfig.gridLines.label.fontSize}
                        fontWeight={chartConfig.gridLines.label.fontWeight}
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                      >
                        {formatStrikePrice(price)}
                      </text>
                    </g>

                    {/* Glowing overlay for split lines when none selected */}
                    {shouldGlow && (
                      <>
                        {isMixedOrders ? (
                          // Mixed orders glow
                          <>
                            <line
                              x1={0}
                              y1={y - 1}
                              x2={centerX - labelHalfWidth}
                              y2={y - 1}
                              stroke="rgba(var(--brand-green-rgb), 0.5)"
                              strokeWidth="3"
                              strokeDasharray="2,2"
                              style={{
                                animation:
                                  'closestStrikePulse 4s ease-in-out infinite',
                                filter:
                                  'drop-shadow(0 0 3px rgba(var(--brand-green-rgb), 0.5)) drop-shadow(0 0 8px rgba(var(--brand-green-rgb), 0.35))',
                              }}
                            />
                            <line
                              x1={0}
                              y1={y + 1}
                              x2={centerX - labelHalfWidth}
                              y2={y + 1}
                              stroke="rgba(var(--brand-red-rgb), 0.5)"
                              strokeWidth="3"
                              strokeDasharray="2,2"
                              style={{
                                animation:
                                  'closestStrikePulse 4s ease-in-out infinite',
                                filter:
                                  'drop-shadow(0 0 3px rgba(var(--brand-red-rgb), 0.5)) drop-shadow(0 0 8px rgba(var(--brand-red-rgb), 0.35))',
                              }}
                            />
                            <line
                              x1={centerX + labelHalfWidth}
                              y1={y - 1}
                              x2={innerWidth}
                              y2={y - 1}
                              stroke="rgba(var(--brand-green-rgb), 0.5)"
                              strokeWidth="3"
                              strokeDasharray="2,2"
                              style={{
                                animation:
                                  'closestStrikePulse 4s ease-in-out infinite',
                                filter:
                                  'drop-shadow(0 0 3px rgba(var(--brand-green-rgb), 0.5)) drop-shadow(0 0 8px rgba(var(--brand-green-rgb), 0.35))',
                              }}
                            />
                            <line
                              x1={centerX + labelHalfWidth}
                              y1={y + 1}
                              x2={innerWidth}
                              y2={y + 1}
                              stroke="rgba(var(--brand-red-rgb), 0.5)"
                              strokeWidth="3"
                              strokeDasharray="2,2"
                              style={{
                                animation:
                                  'closestStrikePulse 4s ease-in-out infinite',
                                filter:
                                  'drop-shadow(0 0 3px rgba(var(--brand-red-rgb), 0.5)) drop-shadow(0 0 8px rgba(var(--brand-red-rgb), 0.35))',
                              }}
                            />
                          </>
                        ) : (
                          // Single color orders glow
                          <>
                            <line
                              x1={0}
                              y1={y}
                              x2={centerX - labelHalfWidth}
                              y2={y}
                              stroke={topStrokeColor.replace(
                                opacity.toString(),
                                '0.5',
                              )}
                              strokeWidth="3"
                              strokeDasharray="2,2"
                              style={{
                                animation:
                                  'closestStrikePulse 4s ease-in-out infinite',
                                filter: `drop-shadow(0 0 3px ${topStrokeColor.replace(opacity.toString(), '0.5')}) drop-shadow(0 0 8px ${topStrokeColor.replace(opacity.toString(), '0.35')})`,
                              }}
                            />
                            <line
                              x1={centerX + labelHalfWidth}
                              y1={y}
                              x2={innerWidth}
                              y2={y}
                              stroke={topStrokeColor.replace(
                                opacity.toString(),
                                '0.5',
                              )}
                              strokeWidth="3"
                              strokeDasharray="2,2"
                              style={{
                                animation:
                                  'closestStrikePulse 4s ease-in-out infinite',
                                filter: `drop-shadow(0 0 3px ${topStrokeColor.replace(opacity.toString(), '0.5')}) drop-shadow(0 0 8px ${topStrokeColor.replace(opacity.toString(), '0.35')})`,
                              }}
                            />
                          </>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  // For non-main strikes: Regular full line (always white - no orders)
                  <>
                    <line
                      x1={0}
                      y1={y}
                      x2={innerWidth}
                      y2={y}
                      stroke={`rgba(255, 255, 255, ${opacity})`}
                      strokeDasharray="2,2"
                    />
                    {shouldGlow && (
                      <line
                        x1={0}
                        y1={y}
                        x2={innerWidth}
                        y2={y}
                        stroke="rgba(255, 255, 255, 0.5)"
                        strokeWidth="3"
                        strokeDasharray="2,2"
                        style={{
                          animation:
                            'closestStrikePulse 4s ease-in-out infinite',
                          filter:
                            'drop-shadow(0 0 3px rgba(255, 255, 255, 0.5)) drop-shadow(0 0 8px rgba(255, 255, 255, 0.35))',
                        }}
                      />
                    )}
                  </>
                )}
              </>
            )}
            {labelsOnly && showPriceLabel && (
              <g>
                <rect
                  x={centerX - chartConfig.gridLines.subdivisionLabel.width / 2}
                  y={y - chartConfig.gridLines.subdivisionLabel.height / 2}
                  width={chartConfig.gridLines.subdivisionLabel.width}
                  height={chartConfig.gridLines.subdivisionLabel.height}
                  fill={chartConfig.gridLines.subdivisionLabel.background}
                  rx={chartConfig.gridLines.subdivisionLabel.borderRadius}
                  style={{ pointerEvents: 'none' }}
                />
                <text
                  x={centerX}
                  y={y}
                  fill="rgba(255, 255, 255, 0.4)"
                  fontSize={chartConfig.gridLines.subdivisionLabel.fontSize}
                  textAnchor="middle"
                  dominantBaseline="central"
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {formatStrikePrice(price)}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </>
  );
}

interface ChartAnimationsProps {
  children: React.ReactNode;
}

export function ChartAnimations({ children }: ChartAnimationsProps) {
  return (
    <>
      <defs>
        <style>
          {`
            @keyframes expandingPulse {
              0% { 
                transform: scale(1); 
                opacity: 0.6; 
                stroke-width: 2px;
              }
              100% { 
                transform: scale(4); 
                opacity: 0; 
                stroke-width: 0.5px;
              }
            }
            @keyframes pulse {
              0%, 100% { 
                opacity: .8;
              }
              50% { 
                opacity: 0.7;
              }
            }
            @keyframes closestStrikePulse {
              0%, 100% { 
                opacity: 0.2;
              }
              50% { 
                opacity: 1;
              }
            }
          `}
        </style>
      </defs>
      {children}
    </>
  );
}

interface TimeRangeIndicatorProps {
  timeRangeMs: number;
  width: number;
  height: number;
}

export function TimeRangeIndicator({
  timeRangeMs,
  width,
  height,
}: TimeRangeIndicatorProps) {
  const formatTimeRange = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (hours > 0) {
      if (minutes > 0) {
        return `${hours} hour${hours > 1 ? 's' : ''}, ${minutes} minute${minutes > 1 ? 's' : ''}`;
      }
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
      return '1 minute';
    }
  };

  return (
    <text
      x={width / 2}
      y={height - chartConfig.timeIndicator.yOffset}
      fill="rgba(255, 255, 255, 0.4)"
      fontSize={chartConfig.timeIndicator.fontSize}
      textAnchor="middle"
      dominantBaseline="baseline"
    >
      {formatTimeRange(timeRangeMs)}
    </text>
  );
}
